import { formatRewardNumber, rewardTypeLabel } from './referralProgress';

/**
 * Turn the form's raw row values into the address list POST /referral/invites
 * expects: trimmed, blank rows dropped, duplicates collapsed. Row order is
 * otherwise preserved.
 *
 * @param {string[]} [rows]
 * @returns {string[]}
 */
export function buildInvitePayload(rows) {
  const trimmed = (rows || []).map((row) => (row || '').trim()).filter(Boolean);
  return Array.from(new Set(trimmed));
}

// POST /referral/invites reports every address it didn't create as skipped, with a
// reason. `already_invited` is the one that isn't a failure: this referrer already has a
// live referral row for it, so it counts toward the program just like a fresh one.
const SKIP_REASON_ALREADY_INVITED = 'already_invited';

// Skips where no referral was ever going to come of the address — it's malformed, or it
// belongs to someone already on Diffit — rather than one that failed. Rendered neutrally,
// so the tone red carries is reserved for the latter.
const UNUSABLE_SKIP_REASONS = new Set(['invalid_format', 'blank', 'duplicate', 'already_a_user']);

/** The API lowercases the addresses it stores, so every comparison here does too. */
export function normalizeAddress(email) {
  return (email || '').trim().toLowerCase();
}

/**
 * Fold one POST /referral/invites response into the running per-address outcome map.
 *
 * Cumulative, because a response only describes the batch it was given: an address
 * accepted on an earlier submit is absent from the next response's `created`, so reading
 * the latest response alone would drop its accepted state.
 *
 * @param {Object<string, 'accepted'|'rejected'|'invalid'>} previous
 * @param {{created?: string[], skipped?: {email: string, reason: string}[]}} response
 * @returns {Object<string, 'accepted'|'rejected'|'invalid'>} keyed by normalized address
 */
export function mergeInviteOutcomes(previous, response) {
  const merged = { ...(previous || {}) };
  (response?.created || []).forEach((email) => {
    const key = normalizeAddress(email);
    if (key) merged[key] = 'accepted';
  });
  (response?.skipped || []).forEach((skip) => {
    const key = normalizeAddress(skip?.email);
    if (!key) return;
    if (skip?.reason === SKIP_REASON_ALREADY_INVITED) {
      merged[key] = 'accepted';
    } else {
      merged[key] = UNUSABLE_SKIP_REASONS.has(skip?.reason) ? 'invalid' : 'rejected';
    }
  });
  return merged;
}

/**
 * The Mixpanel properties every "Invites Sent" event carries about one POST
 * /referral/invites round trip. `skip_reasons` is the API's reason for each address it
 * didn't create, one entry per address. A Mixpanel breakdown on it counts each send once
 * per distinct reason (so the `self` bucket is sends with at least one self-invite, not
 * self-invited addresses); `skipped_count` carries the address total. Together they are
 * the only way to tell a self-invite from a typo or a re-send after the fact.
 *
 * @param {string[]} [sent] the addresses this request posted
 * @param {{created?: string[], skipped?: {email: string, reason: string}[]}} [response]
 * @returns {{invite_count: number, created_count: number, skipped_count: number, skip_reasons: string[]}}
 */
export function inviteSendTrackingProps(sent, response) {
  const skipped = response?.skipped || [];
  return {
    invite_count: (sent || []).length,
    created_count: (response?.created || []).length,
    skipped_count: skipped.length,
    skip_reasons: skipped.map((skip) => skip?.reason || 'unknown'),
  };
}

/**
 * The addresses the API hasn't answered for yet — the only ones a resubmit should send.
 *
 * Re-sending one it already answered comes back skipped/already_invited, and it counts
 * against the per-request address cap that new invites need.
 *
 * @param {string[]} [emails]
 * @param {Object<string, 'accepted'|'rejected'|'invalid'>} [outcomes]
 * @returns {string[]}
 */
export function unsentAddresses(emails, outcomes) {
  return (emails || []).filter((email) => !(outcomes || {})[normalizeAddress(email)]);
}

/**
 * The numerator of the field's "n of threshold" counter: how many addresses count toward
 * the program right now.
 *
 * Before the first submit the chips are all there is, so it's an optimistic projection of
 * them. Once the API has answered, its qualified_count is authoritative — it leaves out
 * the addresses it rejected, and it includes referrals made before this wizard run, which
 * the field knows nothing about. Anything typed since is added on as the same projection.
 *
 * @param {{emails?: string[], outcomes?: Object<string, 'accepted'|'rejected'|'invalid'>,
 *   progress?: {qualified_count: number}|null}} args
 * @returns {number}
 */
export function qualifyingInviteCount({ emails, outcomes, progress }) {
  if (!progress) return (emails || []).length;
  return progress.qualified_count + unsentAddresses(emails, outcomes).length;
}

/**
 * @param {{created: number, threshold: number|null|undefined}} args - `created` is the
 *   number of addresses actually invited in the last submit; `threshold` is the
 *   program's next-tier threshold.
 * @returns {string|null} A nudge like "3 of 5 — add 2 more to unlock your reward", or
 *   null when there's no threshold to fall short of, or it's already been met.
 */
// `qualified` must be the CUMULATIVE count across every attempt (the API's
// progress.qualified_count), not the batch just submitted — inviting 2 then 3 against a
// threshold of 5 has to read "5 of 5", not "3 of 5".
export function shortfallMessage({ qualified, threshold }) {
  if (!threshold || qualified >= threshold) return null;
  const remaining = threshold - qualified;
  return `${qualified} of ${threshold} — add ${remaining} more to unlock your reward`;
}

/**
 * Whether the campaign `key` names can still pay this sharer a tier reward — the one question the
 * bonus footnote (see components/modals/ReferralBonusFootnote) is gated on.
 *
 * Keyed, not merely "any program": the footnote states one campaign's terms, so a tier from a
 * different campaign is not evidence those terms apply. `program.key` is the campaign
 * `referral-program-configuration` currently points at that program (see
 * async_program_keys_by_name in diffit-api) — which is what makes the question answerable from
 * this payload at all, rather than inferred from the LaunchDarkly flag, which only says a
 * campaign exists somewhere and not whose tier this is.
 *
 * `programs` is per-teacher: async_get_assigned_programs joins referral_assignment on user_id, so
 * it is empty for a teacher no campaign has enrolled, and `next_tier: null` means they have
 * already earned that program's whole ladder. Either way there is no payout left to promise.
 *
 * Deliberately blind to qualifying_step. Advertising the bonus doesn't care when it pays, and
 * filtering on 'invited' once made the callout vanish the day its program moved to 'generated'.
 *
 * @param {Array<{key?: string, next_tier?: {threshold?: number}}>} [programs]
 * @param {string} key
 * @returns {boolean}
 */
export function hasPayableBonusTier(programs, key) {
  if (!key) return false;
  return (programs || []).some(
    (program) => program?.key === key && !!program?.next_tier?.threshold
  );
}

// Which payload key a claimable reward needs, per reward_type — mirrors the
// referrer-side tier grant shape POST /referral/invites can return.
export function isClaimableReward(reward) {
  if (!reward) return false;
  const { reward_type: rewardType, amount, payload } = reward;
  if (rewardType === 'template') return !!payload?.template_url;
  if (rewardType === 'promo_code') return !!payload?.code;
  if (rewardType === 'trial_days' || rewardType === 'swag_credit') {
    return amount !== null && amount !== undefined;
  }
  return false;
}

/**
 * Split raw typed or pasted text into candidate addresses. Commas, semicolons, and
 * newlines separate entries; a display name around a bracketed address is stripped, and
 * an entry with no brackets splits on whitespace too so a bare space-separated list still
 * works. Empty pieces are dropped.
 *
 * The bracket handling is what makes the "paste a column out of a staff directory" case
 * work: Outlook and Gmail both copy as `John Doe <john@school.org>`, and splitting that
 * on whitespace yields `John`, `Doe`, and `<john@school.org>` — the last of which passes
 * an email regex with the brackets still attached, so it would be sent as-is.
 *
 * @param {string} [text]
 * @returns {string[]}
 */
export function splitAddressInput(text) {
  return (text || '')
    .split(/[,;\n]+/)
    .map((entry) => {
      const bracketed = entry.match(/<([^<>]+)>/);
      return bracketed ? bracketed[1] : entry;
    })
    .flatMap((entry) => entry.split(/\s+/))
    .map((token) => token.trim())
    .filter(Boolean);
}

/**
 * The clause completing "Share Diffit with N other teachers and …".
 *
 * Reward-type specific rather than one "we'll send you X" template, because a single
 * verb doesn't fit all four types: swag and templates get sent, but trial days get
 * added to an account ("we'll send you 14 trial days" reads wrong).
 *
 * @param {string} rewardType
 * @param {number|string|null|undefined} amount
 * @returns {string}
 */
export function rewardPromise(rewardType, amount) {
  const number = formatRewardNumber(rewardType, amount);
  if (rewardType === 'trial_days' && number !== null) {
    return `we'll add ${number} day${Number(amount) === 1 ? '' : 's'} to your free trial`;
  }
  if (rewardType === 'swag_credit' && number !== null) {
    return `we'll send you ${number} in swag credit`;
  }
  if (rewardType === 'template')
    return "we'll send you a collection of community favorite activity templates";
  // Applied to the account on earning, not handed over to redeem — see _async_apply_reward.
  if (rewardType === 'promo_code') return "we'll apply a promo code to your account";
  // Unrecognized type (or a known one missing its amount): fall back to the label so
  // the sentence still completes rather than rendering a dangling comma.
  return `we'll send you ${rewardTypeLabel(rewardType).toLowerCase()}`;
}

/**
 * The invite step's subheading: the ask. The framing sentence it used to lead with now
 * runs as the step title, so this is the one line beneath it.
 *
 * @param {{threshold: number, rewardType: string, rewardAmount: number|string|null|undefined}} nextTier
 * @returns {string}
 */
export function inviteStepSubheading({ threshold, rewardType, rewardAmount }) {
  const teacherWord = threshold === 1 ? 'teacher' : 'teachers';
  return `Share Diffit with ${threshold} other ${teacherWord} and ${rewardPromise(rewardType, rewardAmount)}.`;
}
